// WebSocket 连接池管理器
// 用于管理全局 WebSocket 连接，避免重复创建相同 URL 的连接

/**
 * WebSocket 连接池管理器
 * 单例模式，用于全局管理 WebSocket 连接，避免重复创建
 */
class WebSocketPool {
  private static instance: WebSocketPool
  // 存储所有活跃的 WebSocket 连接
  private connections: Map<
    string,
    {
      ws: WebSocket
      refCount: number // 引用计数，记录有多少个地方使用了这个连接
      listeners: Map<keyof WebSocketEventMap, Set<any>> // 存储每个连接的事件监听器
    }
  > = new Map()

  // 定时清理检查间隔
  private readonly CHECK_INTERVAL = 60000 // 60秒

  private constructor() {
    // 定期检查并清理不活跃的连接
    setInterval(() => this.cleanupInactiveConnections(), this.CHECK_INTERVAL)

    // 页面卸载时关闭所有连接
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => {
        this.closeAllConnections()
      })
    }
  }

  /**
   * 获取 WebSocketPool 单例
   */
  public static getInstance(): WebSocketPool {
    if (!WebSocketPool.instance) {
      WebSocketPool.instance = new WebSocketPool()
    }
    return WebSocketPool.instance
  }

  /**
   * 获取或创建 WebSocket 连接
   * @param url WebSocket URL
   * @param protocols WebSocket 协议
   * @returns WebSocket 连接实例
   */
  public getWebSocket(url: string, protocols?: string | string[]): WebSocket {
    const key = this.generateKey(url, protocols)

    // 检查是否有现有连接
    const existingConn = this.connections.get(key)
    if (existingConn) {
      console.log(`[WebSocketPool] 检查现有连接: ${url}`, existingConn.ws)
    }

    // 如果有现有连接且状态正常，直接返回
    if (existingConn && this.isConnectionHealthy(existingConn.ws)) {
      console.log(`[WebSocketPool] 复用已有连接: ${url}`)
      existingConn.refCount++
      return existingConn.ws
    }

    // 如果有连接，但状态不正常，关闭它
    /*    if (existingConn) {
      console.log('closing existing connection due to unhealthy state:', existingConn.ws.readyState)
      this.closeConnection(key)
    } */

    // 创建新连接
    console.log(`[WebSocketPool] 创建新连接: ${url}`)
    const ws = protocols ? new WebSocket(url, protocols) : new WebSocket(url)

    // 存储连接
    this.connections.set(key, {
      ws,
      refCount: 1,
      listeners: new Map(),
    })

    // 重写 addEventListener 和 removeEventListener 方法，以便跟踪所有事件监听器
    const originalAddEventListener = ws.addEventListener
    const originalRemoveEventListener = ws.removeEventListener

    ws.addEventListener = (
      type: string,
      listener: EventListenerOrEventListenerObject,
      options?: boolean | AddEventListenerOptions
    ) => {
      // 存储监听器到池中
      const listeners = this.connections.get(key)?.listeners
      if (listeners) {
        if (!listeners.has(type as keyof WebSocketEventMap)) {
          listeners.set(type as keyof WebSocketEventMap, new Set())
        }
        listeners.get(type as keyof WebSocketEventMap)?.add(listener)
      }

      // 调用原始方法
      return originalAddEventListener.call(ws, type, listener, options)
    }

    ws.removeEventListener = (
      type: string,
      listener: EventListenerOrEventListenerObject,
      options?: boolean | EventListenerOptions
    ) => {
      // 从池中移除监听器
      const listeners = this.connections.get(key)?.listeners
      if (listeners && listeners.has(type as keyof WebSocketEventMap)) {
        listeners.get(type as keyof WebSocketEventMap)?.delete(listener)
      }

      // 调用原始方法
      return originalRemoveEventListener.call(ws, type, listener, options)
    }

    return ws
  }

  /**
   * 释放 WebSocket 连接（减少引用计数）
   * @param url WebSocket URL
   * @param protocols WebSocket 协议
   */
  public releaseWebSocket(url: string, protocols?: string | string[]): void {
    const key = this.generateKey(url, protocols)
    const conn = this.connections.get(key)

    if (conn) {
      conn.refCount--
      console.log(`[WebSocketPool] 释放连接: ${url}, 剩余引用: ${conn.refCount}`)

      // 如果没有引用了，可以考虑关闭连接
      if (conn.refCount <= 0) {
        // 为了避免过早关闭，这里给一个延迟，可以根据需要调整或移除
        setTimeout(() => {
          const currentConn = this.connections.get(key)
          if (currentConn && currentConn.refCount <= 0) {
            this.closeConnection(key)
          }
        }, 5000)
      }
    }
  }

  /**
   * 关闭特定连接
   * @param key 连接的键值
   */
  private closeConnection(key: string): void {
    const conn = this.connections.get(key)
    if (conn) {
      console.log(`[WebSocketPool] 关闭连接: ${key}`)

      // 如果连接开着，关闭它
      if (conn.ws.readyState === WebSocket.OPEN || conn.ws.readyState === WebSocket.CONNECTING) {
        conn.ws.close()
      }

      // 从池中移除
      this.connections.delete(key)
    }
  }

  /**
   * 清理不活跃的连接
   */
  private cleanupInactiveConnections(): void {
    for (const [key, conn] of this.connections.entries()) {
      // 如果连接已关闭或者引用计数为0，清理它
      if (conn.ws.readyState === WebSocket.CLOSED || conn.ws.readyState === WebSocket.CLOSING || conn.refCount <= 0) {
        this.closeConnection(key)
      }
    }
  }

  /**
   * 关闭所有连接
   */
  public closeAllConnections(): void {
    for (const key of this.connections.keys()) {
      this.closeConnection(key)
    }
  }

  /**
   * 检查连接是否健康
   * @param ws WebSocket 连接
   * @returns 是否健康
   */
  private isConnectionHealthy(ws: WebSocket): boolean {
    return ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING
  }

  /**
   * 生成连接的唯一键值
   * @param url WebSocket URL
   * @param protocols WebSocket 协议
   * @returns 唯一键值
   */
  private generateKey(url: string, protocols?: string | string[]): string {
    if (!protocols) {
      return url
    }

    if (Array.isArray(protocols)) {
      return `${url}|${protocols.join(',')}`
    }

    return `${url}|${protocols}`
  }

  /**
   * 获取当前连接数量
   * @returns 连接数量
   */
  public getConnectionCount(): number {
    return this.connections.size
  }

  /**
   * 检查是否有特定URL的连接
   * @param url WebSocket URL
   * @param protocols WebSocket 协议
   * @returns 是否存在连接
   */
  public hasConnection(url: string, protocols?: string | string[]): boolean {
    const key = this.generateKey(url, protocols)
    const conn = this.connections.get(key)
    return !!conn && this.isConnectionHealthy(conn.ws)
  }
}

// 导出单例获取方法
export const getWebSocketPool = () => {
  if (typeof window !== 'undefined') {
    return WebSocketPool.getInstance()
  }
  return null
}

export default WebSocketPool
